// ConfirmDelete — Phase 5 Plan 05-03 (GDP-04, D-83).
// Phase 12 Plan 12-10: migrated to @telegram-apps/telegram-ui shell.
//
// Static landing page targeted by Postmark email confirmation link:
//   https://app.silentdispatcher.com/gdpr/confirm-delete?token=<plaintext>
//
// This page IS the second confirmation step of the GDPR delete flow
// (bot /delete_my_data → email link → this token-bound landing). Token-logic
// (parse → POST → strip token) is NOT touched (UI-SPEC §10); only the shell is
// migrated. Browser-locale resolution unchanged (gdpr namespace, no auth).
//
// Flow:
//   1. Mount → parse URLSearchParams for `token`.
//   2. POST /api/v1/gdpr/confirm-delete?token=<token> via gdprApi.confirmDelete.
//   3. history.replaceState() to strip the token from the URL (T-05-03-12).
//   4. Render one of 5 states based on API response:
//        pending           — POST in flight
//        confirmed         — show "Scheduled for {date+7d}; use /cancel_deletion"
//        expired           — 410 ("Token expired")
//        invalid           — 404 ("Invalid token")
//        already_executed  — 409 ("Already executed or cancelled")
//        network_error     — connectivity failure
//
// No authentication required — Mini App opens this route via direct deep
// link from email, NOT through the Telegram Mini App SDK flow.

import { Banner, Cell, List, Section, Title } from "@telegram-apps/telegram-ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { type GdprConfirmStatus, confirmDelete } from "../api/gdpr";

export interface ConfirmDeleteProps {
  /** API base URL, normally from VITE_API_BASE_URL or AppProps.baseUrl */
  baseUrl: string;
}

const DESTRUCTIVE_COLOR =
  "var(--tgui--destructive_text_color, var(--tg-theme-destructive-text-color, #DC2626))";
const HINT_COLOR = "var(--tgui--hint_color, var(--tg-theme-hint-color, #475569))";

export function ConfirmDelete({ baseUrl }: ConfirmDeleteProps): React.JSX.Element {
  const { t } = useTranslation("gdpr");
  const [status, setStatus] = useState<GdprConfirmStatus>("pending");
  const [scheduledFor, setScheduledFor] = useState<string | undefined>();

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setStatus("invalid");
      return () => controller.abort();
    }

    void (async () => {
      const result = await confirmDelete(baseUrl, token, controller.signal);
      // W-009: ignore aborted requests — component unmounted, setState
      // would warn в dev и не имеет смысла в prod (юзеру нечего показать).
      if (result.status === "aborted") {
        return;
      }
      setStatus(result.status);
      if (result.scheduled_for) {
        setScheduledFor(result.scheduled_for);
      }
      // T-05-03-12: strip token from URL after POST so referrer headers /
      // browser history don't leak it.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.toString());
      } catch {
        // ignore — older browsers / iframe sandbox
      }
    })();

    return () => controller.abort();
  }, [baseUrl]);

  return (
    <List style={{ maxWidth: 480, margin: "0 auto" }}>
      <Section>
        <div style={{ padding: 16 }}>
          <Title level="2">{t("confirm_delete.title", "Confirm deletion of your data")}</Title>
        </div>

        {status === "pending" && (
          <Cell multiline>{t("confirm_delete.pending", "Confirming…")}</Cell>
        )}

        {status === "confirmed" && (
          <>
            <Banner
              type="section"
              header={t(
                "confirm_delete.confirmed",
                "Confirmed. Your data will be deleted in 7 days.",
              )}
              subheader={
                scheduledFor
                  ? `${t("confirm_delete.scheduled_for_label", "Scheduled for")} ${new Date(
                      scheduledFor,
                    ).toLocaleString()}`
                  : undefined
              }
            />
            <Cell multiline style={{ color: HINT_COLOR }}>
              {t(
                "confirm_delete.cancel_via_bot_hint",
                "Send /cancel_deletion in the Telegram bot to abort.",
              )}
            </Cell>
          </>
        )}

        {status === "expired" && (
          <Cell multiline style={{ color: DESTRUCTIVE_COLOR }}>
            {t("confirm_delete.expired", "Token expired. Send /delete_my_data in the bot again.")}
          </Cell>
        )}

        {status === "invalid" && (
          <Cell multiline style={{ color: DESTRUCTIVE_COLOR }}>
            {t("confirm_delete.invalid", "Invalid token.")}
          </Cell>
        )}

        {status === "already_executed" && (
          <Cell multiline style={{ color: HINT_COLOR }}>
            {t(
              "confirm_delete.already_executed",
              "Deletion already processed (executed or cancelled).",
            )}
          </Cell>
        )}

        {status === "network_error" && (
          <Cell multiline style={{ color: DESTRUCTIVE_COLOR }}>
            {t("confirm_delete.network_error", "Network error. Please try again.")}
          </Cell>
        )}
      </Section>
    </List>
  );
}
